import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Slider,
  Checkbox,
  Radio,
  Switch,
} from "@eweadn-qiankun/shared-components";
import type {
  CommunityPreviewDeviceMeta,
  FullBoardConfigData,
} from "@eweadn-qiankun/shared-types";
import {
  getPerformanceKeyDelayOptions,
  getPerformancePollingOptions,
  getPerformancePreviewValues,
  resolvePerformancePreviewCards,
  resolveWasdArrowPreview,
} from "../utils/boardConfigPreviewUtils";

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: FullBoardConfigData;
}

export default function KeyboardPerformanceSection({
  meta,
  configData,
}: Props) {
  const { t } = useTranslation();
  const cards = useMemo(() => resolvePerformancePreviewCards(meta), [meta]);
  const values = useMemo(
    () => getPerformancePreviewValues(configData, meta),
    [configData, meta]
  );
  const pollingOptions = useMemo(
    () => getPerformancePollingOptions(meta),
    [meta]
  );
  const keyDelayOptions = useMemo(() => getPerformanceKeyDelayOptions(), []);
  const wasdPreview = useMemo(
    () => resolveWasdArrowPreview(meta, configData),
    [meta, configData]
  );
  const sleepSliderValue = values.neverSleep ? 0 : values.sleepMinutes;
  const sleepUnitIsSeconds = values.sleepUnit === "seconds";
  const sleepUnitLabel = sleepUnitIsSeconds
    ? t("keyboard.performance.secondUnitShort", { defaultValue: "s" })
    : t("keyboard.performance.minuteUnitShort");
  const sleepTitle = sleepUnitIsSeconds
    ? t("mouse.parameter.sleepTime")
    : t("keyboard.performance.sleepSettingTitle");
  const sleepDesc = sleepUnitIsSeconds
    ? t("mouse.parameter.sleepTimeDescBeiying")
    : t("keyboard.performance.sleepSettingDesc");
  const rongyuanSleepDesc = `${t("keyboard.performance.sleepSettingDesc")}${t(
    "keyboard.performance.rongyuan.sleepZeroHint"
  )}`;

  return (
    <div className="community-kb-performance-preview performance">
      {values.rongyuanSleepSections?.map((section) => (
        <div className="performance-section" key={section.field}>
          <div>
            <div className="performance-section-title">
              {t(section.titleKey)}
            </div>
            <div className="performance-section-description">
              {rongyuanSleepDesc}
            </div>
          </div>
          <div className="radio-group sleep-time">
            <div className="community-preview-inline-row">
              <div className="parameter-slider-wrap">
                <Slider
                  value={section.neverSleep ? 0 : section.minutes}
                  onChange={() => undefined}
                  min={0}
                  max={60}
                  step={1}
                  showValue
                  unit={t("keyboard.performance.minuteUnitShort")}
                  className="slider-box"
                  disabled={section.neverSleep}
                />
              </div>
              <Checkbox
                label={t("keyboard.sleep.never")}
                checked={section.neverSleep}
                shape="circle"
                className="checkbox-container"
                onChange={() => undefined}
              />
            </div>
          </div>
        </div>
      ))}

      {cards.showSleep ? (
        <div className="performance-section">
          <div>
            <div className="performance-section-title">{sleepTitle}</div>
            <div className="performance-section-description">{sleepDesc}</div>
          </div>

          <div className="radio-group sleep-time">
            <div className="community-preview-inline-row">
              <div className="parameter-slider-wrap">
                <Slider
                  value={sleepSliderValue}
                  onChange={() => undefined}
                  min={0}
                  max={sleepUnitIsSeconds ? 3000 : 30}
                  step={sleepUnitIsSeconds ? 30 : 1}
                  showValue
                  unit={sleepUnitLabel}
                  className="slider-box"
                  disabled={values.neverSleep}
                />
              </div>
              <Checkbox
                label={t("keyboard.sleep.never")}
                checked={values.neverSleep}
                shape="circle"
                className="checkbox-container"
                onChange={() => undefined}
              />
            </div>
          </div>
        </div>
      ) : null}

      {cards.showPolling && pollingOptions.length > 0 ? (
        <div className="performance-section">
          <div>
            <div className="performance-section-title">
              {t("keyboard.performance.reportRateTitle")}
            </div>
            <div className="performance-section-description">
              {t("keyboard.performance.reportRateDesc")}
            </div>
          </div>
          <div className="radio-group polling-rate">
            {pollingOptions.map((option) => (
              <Radio
                key={option}
                label={option}
                value={option}
                checked={values.pollingRate === option}
                onChange={() => undefined}
              />
            ))}
          </div>
        </div>
      ) : null}

      {cards.showKeyDelay ? (
        <div className="performance-section">
          <div>
            <div className="performance-section-title">
              {t("keyboard.performance.keyDelayTitle")}
            </div>
            <div className="performance-section-description">
              {t("keyboard.performance.keyDelayDesc")}
            </div>
          </div>
          <div className="radio-group polling-rate">
            {keyDelayOptions.map((option, index) => (
              <Radio
                key={option}
                label={option}
                value={option}
                checked={values.keyDelayLevel === index + 1}
                onChange={() => undefined}
              />
            ))}
          </div>
        </div>
      ) : null}

      {cards.showDebounceMode ? (
        <div className="performance-section">
          <div>
            <div className="performance-section-title">
              {t("keyboard.performance.debounceMode")}
            </div>
            <div className="performance-section-description">
              {t("keyboard.performance.reportRateDesc")}
            </div>
          </div>
          <div className="radio-group debounce-mode">
            {(
              [
                "keyboard.performance.debounceNormal",
                "keyboard.performance.debouncePre",
                "keyboard.performance.debouncePost",
                "keyboard.performance.debounceAuto",
              ] as const
            ).map((labelKey) => (
              <Radio
                key={labelKey}
                label={t(labelKey)}
                value={labelKey}
                checked={values.debounceMode === labelKey}
                onChange={() => undefined}
              />
            ))}
          </div>
        </div>
      ) : null}

      {cards.showDebounceTime ? (
        <div className="performance-section">
          <div>
            <div className="performance-section-title">
              {t("keyboard.performance.debounceTime")}
            </div>
            <div className="performance-section-description">
              {t("keyboard.performance.reportRateDesc")}
            </div>
          </div>
          <div className="radio-group debounce-setting">
            <div className="parameter-slider-wrap">
              <Slider
                value={values.debounceTimeMs ?? 1}
                onChange={() => undefined}
                min={1}
                max={50}
                showValue
                unit="ms"
                className="slider-box debounce-setting"
              />
            </div>
          </div>
        </div>
      ) : null}

      {cards.showWinLock ? (
        <div className="performance-section">
          <div className="performance-section-header">
            <div>
              <div className="performance-section-title">
                {t("keyboard.performance.winLockTitle")}
              </div>
              <div className="performance-section-description">
                {t("keyboard.performance.winLockDesc")}
              </div>
            </div>
            <Switch checked={values.winLock} onChange={() => undefined} />
          </div>
        </div>
      ) : null}

      {cards.showMacMode ? (
        <div className="performance-section">
          <div className="performance-section-header">
            <div>
              <div className="performance-section-title">
                {t("keyboard.performance.macModeTitle")}
              </div>
              <div className="performance-section-description">
                {t("keyboard.performance.macModeDesc")}
              </div>
            </div>
            <Switch checked={values.macMode} onChange={() => undefined} />
          </div>
        </div>
      ) : null}

      {cards.showRongyuanDebounce ? (
        <div className="performance-section">
          <div>
            <div className="performance-section-title">
              {t("keyboard.performance.rongyuan.debounceTitle")}
            </div>
            <div className="performance-section-description">
              {t("keyboard.performance.keyDelayDesc")}
            </div>
          </div>
          <div className="radio-group debounce-setting">
            <div className="parameter-slider-wrap">
              <Slider
                value={values.rongyuanDebounce ?? 1}
                onChange={() => undefined}
                min={1}
                max={10}
                showValue
                className="slider-box debounce-setting"
              />
            </div>
          </div>
        </div>
      ) : null}

      {cards.showRongyuanLowPower ? (
        <div className="performance-section">
          <div className="performance-section-header">
            <div>
              <div className="performance-section-title">
                {t("keyboard.performance.rongyuan.lowPowerTitle")}
              </div>
              <div className="performance-section-description">
                {t("keyboard.performance.rongyuan.lowPowerDesc")}
              </div>
            </div>
            <Switch
              checked={values.rongyuanLowPowerMode ?? false}
              onChange={() => undefined}
            />
          </div>
        </div>
      ) : null}

      {wasdPreview.visible ? (
        <div className="performance-section">
          <div className="performance-section-header">
            <div>
              <div className="performance-section-title">
                {t("keyboard.performance.wasdArrowSwitchTitle")}
              </div>
              <div className="performance-section-description">
                {t("keyboard.performance.wasdArrowSwitchDesc")}
              </div>
            </div>
            <Switch checked={wasdPreview.swapped} onChange={() => undefined} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
